import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform, useVelocity } from 'framer-motion';

interface VelocitySkewProps {
  children: React.ReactNode;
  /** multiplier on the base shear */
  strength?: number;
  className?: string;
}

const MAX_VELOCITY = 2600;
const MAX_SKEW = 2.2;

/**
 * Scroll-velocity shear whose direction depends on where the element sits
 * across the viewport: content left of centre leans one way, content right of
 * centre leans the other, so the page bends rather than tilting as one slab.
 * Elements on the centre line barely move.
 */
const VelocitySkew: React.FC<VelocitySkewProps> = ({ children, strength = 1, className = '' }) => {
  const ref = useRef<HTMLDivElement>(null);

  const { scrollY } = useScroll();
  const velocity = useVelocity(scrollY);
  const smooth = useSpring(velocity, { stiffness: 320, damping: 60, mass: 0.4 });

  // -1 at the left edge, 0 at centre, +1 at the right edge
  const side = useMotionValue(0);

  useEffect(() => {
    const measure = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r || !window.innerWidth || !r.width) return;
      const centre = (r.left + r.width / 2) / window.innerWidth;
      side.set(Math.max(-1, Math.min(1, (centre - 0.5) * 2)));
    };
    measure();
    const t = setTimeout(measure, 300);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [side]);

  const skewY = useTransform([smooth, side], ([v, s]: number[]) => {
    const clamped = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v));
    return (clamped / MAX_VELOCITY) * MAX_SKEW * s * strength;
  });

  return (
    <motion.div ref={ref} style={{ skewY }} className={className}>
      {children}
    </motion.div>
  );
};

export default VelocitySkew;
